import { Box, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Booking } from "src/business/booking";
import { InfoCard } from "src/design";
import { colorTextMuted } from "src/design/tokens";
import { CardLink } from "./CardLink";
import { TodaysBookingRow } from "./TodaysBookingRow";

/** Enough rows to read the service at a glance; the rest live on Bookings. */
const MAX_ROWS = 5;

interface TodaysBookingsCardProps {
  restaurantId: string;
  bookings: Booking[];
  expectedSeats: number;
  isLoading: boolean;
}

export const TodaysBookingsCard = ({
  restaurantId,
  bookings,
  expectedSeats,
  isLoading,
}: TodaysBookingsCardProps) => {
  const { t } = useTranslation();

  const rows = [...bookings]
    .sort((left, right) =>
      dayjs(left.bookingDate).valueOf() - dayjs(right.bookingDate).valueOf(),
    )
    .slice(0, MAX_ROWS);

  return (
    <InfoCard
      title={t("dashboard.todaysBookings.title")}
      subtitle={t("dashboard.todaysBookings.subtitle", {
        date: dayjs().format("dddd, MMMM D"),
        count: expectedSeats,
      })}
      action={
        <CardLink
          to={`/restaurant/${restaurantId}/bookings`}
          label={t("dashboard.seeAll")}
        />
      }
    >
      {isLoading ? (
        <Box>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={52}
              sx={{ mb: 1.5 }}
            />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ fontSize: "14px", color: colorTextMuted, py: 2 }}>
          {t("dashboard.todaysBookings.empty")}
        </Typography>
      ) : (
        rows.map((booking, index) => (
          <TodaysBookingRow
            key={booking.id}
            booking={booking}
            isLast={index === rows.length - 1}
          />
        ))
      )}
    </InfoCard>
  );
};
